import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth/store";
import { instantiateTemplate, templatesApi } from "../api";
import { templateIcon } from "../icons";

export type NewAssistantContent = {
  name: string;
  firstMessage: string;
  systemPrompt: string;
  templateId: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the instantiated content once Create Assistant is clicked. */
  onCreate: (content: NewAssistantContent) => void;
  /** True while the parent is persisting the new assistant. */
  creating?: boolean;
};

const DEFAULT_NAME = "New Assistant";

/**
 * Screen 1 — "Create Assistant" modal.
 *
 * The quickstart grid shows the published Assistant Default Templates
 * curated in Django admin. Picking one pre-fills the assistant name with the
 * template's suggested persona; Create instantiates First Message + System
 * Prompt with the tenant's real values filled in.
 */
export default function CreateAssistantDialog({
  open,
  onOpenChange,
  onCreate,
  creating: saving = false,
}: Props) {
  const [name, setName] = useState(DEFAULT_NAME);
  const [selected, setSelected] = useState<string>("blank");
  const [creating, setCreating] = useState(false);
  const companyName = useAuthStore((s) => s.user?.tenant?.name) ?? "";

  const templatesQ = useQuery({
    queryKey: ["assistant-quickstart-templates"],
    queryFn: templatesApi.quickstart,
    enabled: open,
    staleTime: 60_000,
  });
  const templates = templatesQ.data ?? [];

  function pickTemplate(id: string, suggestedName: string) {
    setSelected(id);
    // Vapi behavior: picking a template swaps the name for its persona.
    if (id === "blank") {
      setName(DEFAULT_NAME);
    } else if (suggestedName) {
      setName(suggestedName);
    }
  }

  async function handleCreate() {
    const finalName = name.trim() || DEFAULT_NAME;
    if (selected === "blank") {
      onCreate({ name: finalName, firstMessage: "", systemPrompt: "", templateId: null });
      return;
    }
    setCreating(true);
    try {
      const tpl = await templatesApi.detail(selected);
      onCreate({
        name: finalName,
        firstMessage: instantiateTemplate(tpl.greeting, finalName, companyName),
        systemPrompt: instantiateTemplate(tpl.system_prompt, finalName, companyName),
        templateId: tpl.id,
      });
    } catch {
      toast.error("Couldn't load the template. Please try again.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Plus className="size-4 text-primary" />
            Create Assistant
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5">
          <div>
            <h3 className="text-base font-semibold">Choose a template</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Here's a few templates to get you started, or you can create your
              own template and use it to create a new assistant.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>
              Assistant Name{" "}
              <span className="font-normal italic text-amber-500">
                (This can be adjusted at any time after creation.)
              </span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={DEFAULT_NAME}
            />
          </div>

          {/* Blank template */}
          <button
            type="button"
            onClick={() => pickTemplate("blank", "")}
            className={cn(
              "flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors",
              selected === "blank"
                ? "border-primary ring-1 ring-primary"
                : "hover:border-foreground/30",
            )}
          >
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border">
              <Plus className="size-3.5" />
            </span>
            <span>
              <span className="block text-sm font-medium">Blank Template</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                This blank slate template with minimal configurations. It's a
                starting point for creating your custom assistant.
              </span>
            </span>
          </button>

          {/* Quickstart grid — published Assistant Default Templates */}
          <div className="rounded-lg border p-3">
            <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Quickstart
            </div>
            {templatesQ.isLoading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : templates.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">
                No quickstart templates published yet.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {templates.map((tpl) => {
                  const Icon = templateIcon(tpl.icon, tpl.category);
                  const active = selected === tpl.id;
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => pickTemplate(tpl.id, tpl.suggested_name)}
                      className={cn(
                        "flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors",
                        active
                          ? "border-primary ring-1 ring-primary"
                          : "hover:border-foreground/30",
                      )}
                    >
                      <Icon className="size-5 text-muted-foreground" />
                      <div className="text-sm font-medium leading-tight">
                        {tpl.name}
                      </div>
                      <div className="text-xs leading-snug text-muted-foreground">
                        {tpl.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleCreate} disabled={creating || saving}>
            {(creating || saving) && <Loader2 className="size-4 animate-spin" />}
            Create Assistant
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
